// Hook for managing HVAC panel display based on conversation messages
import { useState, useEffect } from 'react';

interface HVACPanelState {
  isOpen: boolean;
  calculationId: string | null;
  projectId: string | null;
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: {
    hvacCalculationId?: string;
    showHVACPanel?: boolean;
    projectId?: string;
    [key: string]: any;
  };
}

export const useHVACPanel = (messages: Message[]) => {
  const [panelState, setPanelState] = useState<HVACPanelState>({
    isOpen: false,
    calculationId: null,
    projectId: null
  });

  useEffect(() => {
    // Check the latest assistant messages for HVAC calculation metadata
    const latestMessages = messages.slice(-5); // Check last 5 messages
    
    // Iterate in reverse order to find the most recent HVAC calculation
    for (let i = latestMessages.length - 1; i >= 0; i--) {
      const message = latestMessages[i];
      if (message.role === 'assistant' && message.metadata) {
        // Check for HVAC calculation trigger
        if (message.metadata.hvacCalculationId && message.metadata.showHVACPanel) {
          setPanelState({
            isOpen: true,
            calculationId: message.metadata.hvacCalculationId,
            projectId: message.metadata.projectId || null
          });
          
          console.log('🏢 HVAC Panel triggered (most recent):', {
            calculationId: message.metadata.hvacCalculationId,
            projectId: message.metadata.projectId,
            messageIndex: i
          });
          
          return; // Found the most recent trigger, stop checking
        }
      }
    }
  }, [messages]);

  const closePanel = () => {
    setPanelState({
      isOpen: false,
      calculationId: null,
      projectId: null
    });
  };

  const openPanel = (calculationId: string, projectId?: string) => {
    setPanelState({
      isOpen: true,
      calculationId,
      projectId: projectId || null
    });
  };

  return {
    ...panelState,
    closePanel,
    openPanel
  };
};

// Helper function to detect HVAC requests in user messages
export const detectHVACRequest = (message: string): boolean => {
  const hvacTriggers = [
    'hvac calculation',
    'hvac load',
    'heating and cooling',
    'load calculation',
    'hvac report',
    'calculate hvac',
    'perform hvac',
    'thermacalc' // MEP agent name
  ];

  const messageContent = message.toLowerCase();
  return hvacTriggers.some(trigger => messageContent.includes(trigger));
};